import loadAllNotificationProducts, {
    ProductNotificationDto,
} from "@aihCustomBlueprint/Handlers/AihCustomProductSubscribe/AllNotificationProducts";
import loadAllProductSubscriptions from "@aihCustomBlueprint/Handlers/AihCustomProductSubscribe/AllProductSubscriptions";
import { ProductAddToListLinkStyles } from "@aihCustomBlueprint/Overrides/Components/ProductAddToListLink";
import { ProductPartNumbersStyles } from "@aihCustomBlueprint/Overrides/Components/ProductPartNumbers";
import CustomApplicationState from "@aihCustomBlueprint/Store/CustomApplicationState";
import ProductSubscriptionItem from "@aihCustomBlueprint/Widgets/ProductSubscription/ProductSubscriptionItem";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import Accordion from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { ImgProps } from "@insite/mobius/Img";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    onClick?: React.EventHandler<React.MouseEvent>;
}

const mapDispatchToProps = {
    loadAllProductSubscriptions,
    loadAllNotificationProducts,
};

const mapStateToProps = (state: CustomApplicationState) => ({
    userName: state.context.session.userName,
});

type Props = ProductCardSelections &
    WidgetProps &
    OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface AllProductSubscriptionStyles {
    container?: GridContainerProps;
    productImage?: ProductImageStyles;
    linkWrappingImage?: ClickablePresentationProps;
    brandGridItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    descriptionGridItem?: GridItemProps;
    productDetailLink?: LinkPresentationProps;
    partNumbersGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    img?: ImgProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    gridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    subscribeButtonBlock?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    cardContainerStyles?: CardContainerStyles;
}

export const allProductSubscriptionStyles: AllProductSubscriptionStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;
        `,
    },
    linkWrappingImage: {
        css: css`
            width: 100%;
        `,
    },
    brandGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            font-size: 20px;
        `,
    },
    productDetailLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
    partNumbersGridItem: {
        width: 12,
    },
    img: {
        css: css`
            height: auto;
            width: 100%;
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    gridContainer: {
        gap: 0,
        css: css`
            padding: 10px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
    },
    subscribeButtonBlock: {
        css: css`
            margin-top: 10px;
        `,
    },
    cardContainerStyles: {
        cardDivider: {
            css: css`
                width: 100%;
            `,
        },
    },
};

const styles = allProductSubscriptionStyles;

const AllProductSubscriptions = ({
    userName,
    loadAllProductSubscriptions,
    loadAllNotificationProducts,
    onClick,
}: Props) => {
    const [notificationProducts, setNotificationProducts] = useState<ProductNotificationDto>();
    const [subscribedProducts, setSubscribedProducts] = useState<ProductModel[]>();

    useEffect(() => {
        loadAllProductSubscriptions({
            onSuccess: result => {
                setSubscribedProducts(result);
            },
            onError: () => {},
        });

        loadAllNotificationProducts({
            onSuccess: result => {
                setNotificationProducts(result);
            },
            onError: () => {},
        });
    }, []);

    if (!userName || !subscribedProducts || !notificationProducts) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="productSubscriptionSpinner" />
            </StyledWrapper>
        );
    }

    return (
        <Accordion headingLevel={2} data-test-selector="allProductSubscriptions">
            <ManagedAccordionSection
                key="inStock"
                title={`${translate("In Stock")} (${notificationProducts?.ProductModel?.length || 0})`}
            >
                {notificationProducts?.ProductModel?.map((product, index) => (
                    <ProductSubscriptionItem
                        key={index}
                        product={product}
                        productInfo={notificationProducts?.ProductInfo?.find(item => item.productId === product.id)}
                        onClick={onClick}
                        styles={allProductSubscriptionStyles}
                    />
                ))}
            </ManagedAccordionSection>
            <ManagedAccordionSection
                key="outOfStock"
                title={`${translate("Out of Stock")} (${subscribedProducts?.length || 0})`}
            >
                {subscribedProducts?.map((product, index) => (
                    <ProductSubscriptionItem
                        key={index}
                        product={product}
                        onClick={onClick}
                        styles={allProductSubscriptionStyles}
                    />
                ))}
            </ManagedAccordionSection>
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AllProductSubscriptions),
    definition: {
        group: "My Account",
        displayName: "All Product Subscription",
    },
};

export default widgetModule;
